// src/features/audit/data/audit.mock.ts
//
// Static presentation data ONLY. There is no audit endpoint wired
// into this monorepo yet — every record below is a placeholder for
// visual layout purposes and must not be read as a real audit log.
// Consumed only through features/audit/hooks/useAuditMock.ts and
// useAuditWorkspaceMock.ts, so there is a single seam to swap for a
// real data source later without touching any component. Same
// shape/rules as features/security/data/security.mock.ts and
// features/commission/data/commission.mock.ts.
//
// This feature intentionally does not import from any other feature —
// entity references (actor name, target name, etc.) are carried as
// plain mock strings, same self-contained approach OrderCustomerCard
// already uses for customerName/customerId.

export type AuditStatus = 'success' | 'warning' | 'failed';

export interface AuditRecord {
  id: string;
  eventTitle: string;
  actorName: string;
  targetName: string;
  status: AuditStatus;
  timestamp: string;
  ip: string;
}

export const auditsMock: AuditRecord[] = [
  {
    id: 'aud-5001',
    eventTitle: 'ورود ادمین به پنل',
    actorName: 'ادمین ارشد',
    targetName: 'پنل ادمین نمایندگی',
    status: 'success',
    timestamp: '۱۰ دقیقه پیش',
    ip: '185.23.14.90',
  },
  {
    id: 'aud-5002',
    eventTitle: 'تغییر سطح دسترسی کاربر',
    actorName: 'ادمین امنیت',
    targetName: 'کاربر: نمایندگی البرز',
    status: 'warning',
    timestamp: '۴۵ دقیقه پیش',
    ip: '78.39.201.4',
  },
  {
    id: 'aud-5003',
    eventTitle: 'تلاش ناموفق ورود',
    actorName: 'ناشناس',
    targetName: 'پنل ادمین ارشد',
    status: 'failed',
    timestamp: '۱ ساعت پیش',
    ip: '46.19.201.55',
  },
  {
    id: 'aud-5004',
    eventTitle: 'ویرایش سیاست کمیسیون',
    actorName: 'ادمین ارشد',
    targetName: 'ساختار کمیسیون سطح ۲',
    status: 'success',
    timestamp: '۳ ساعت پیش',
    ip: '185.23.14.90',
  },
  {
    id: 'aud-5005',
    eventTitle: 'حذف حساب مشتری',
    actorName: 'ادمین پشتیبانی',
    targetName: 'مشتری: cus-1044',
    status: 'warning',
    timestamp: '۵ ساعت پیش',
    ip: '95.81.44.19',
  },
  {
    id: 'aud-5006',
    eventTitle: 'برداشت از کیف پول',
    actorName: 'ادمین مالی',
    targetName: 'کیف پول تننت: تننت گیلان',
    status: 'success',
    timestamp: '۱ روز پیش',
    ip: '31.7.68.201',
  },
  {
    id: 'aud-5007',
    eventTitle: 'مسدودسازی IP مشکوک',
    actorName: 'فایروال برنامه وب',
    targetName: 'IP: 91.207.6.18',
    status: 'success',
    timestamp: '۱ روز پیش',
    ip: '91.207.6.18',
  },
  {
    id: 'aud-5008',
    eventTitle: 'تلاش دسترسی به API خارجی',
    actorName: 'ناشناس',
    targetName: 'ماژول امنیتی: دسترسی API خارجی',
    status: 'failed',
    timestamp: '۲ روز پیش',
    ip: '46.100.12.77',
  },
];

// --- Audit Workspace Foundation --------------------------------------------
//
// Everything below is additional static mock data for the per-record
// workspace route (/audit/:id). Same rule as auditsMock above: no real
// endpoint exists yet, this is presentation-only placeholder data, and
// it is only ever read through
// features/audit/hooks/useAuditWorkspaceMock.ts — components never
// import these maps directly. Keyed by AuditRecord['id'] so the
// workspace hook can do a simple lookup; a record with no entry in a
// given map just renders that card's empty state.

export interface AuditEvent {
  eventType: string;
  action: string;
  result: string;
  timestamp: string;
}

export interface AuditActor {
  name: string;
  role: string;
  tenant: string;
}

export interface AuditTarget {
  targetType: string;
  targetId: string;
  targetName: string;
}

export interface AuditMetadata {
  ip: string;
  userAgent: string;
  extra: string[];
}

export type AuditTimelineEventType = 'info' | 'action' | 'alert' | 'system';

export interface AuditTimelineEvent {
  id: string;
  type: AuditTimelineEventType;
  title: string;
  date: string;
  description: string;
}

export const auditEventMock: Record<string, AuditEvent> = {
  'aud-5001': {
    eventType: 'ورود',
    action: 'ورود موفق با احراز هویت دو مرحله‌ای',
    result: 'موفق',
    timestamp: '1405/04/22 - ۱۰ دقیقه پیش',
  },
  'aud-5002': {
    eventType: 'تغییر دسترسی',
    action: 'ارتقای سطح دسترسی از پشتیبانی به ادمین',
    result: 'نیازمند بازبینی',
    timestamp: '1405/04/22 - ۴۵ دقیقه پیش',
  },
  'aud-5003': {
    eventType: 'ورود',
    action: 'سه تلاش پیاپی ناموفق برای ورود',
    result: 'ناموفق',
    timestamp: '1405/04/22 - ۱ ساعت پیش',
  },
  'aud-5004': {
    eventType: 'ویرایش پیکربندی',
    action: 'تغییر درصد کمیسیون سطح ۲ از ۸٪ به ۱۰٪',
    result: 'موفق',
    timestamp: '1405/04/21 - ۳ ساعت پیش',
  },
  'aud-5005': {
    eventType: 'حذف رکورد',
    action: 'حذف حساب مشتری به دلیل درخواست انصراف',
    result: 'نیازمند بازبینی',
    timestamp: '1405/04/21 - ۵ ساعت پیش',
  },
  'aud-5006': {
    eventType: 'تراکنش مالی',
    action: 'برداشت موجودی از کیف پول تننت',
    result: 'موفق',
    timestamp: '1405/04/20 - ۱ روز پیش',
  },
  'aud-5007': {
    eventType: 'رویداد امنیتی',
    action: 'مسدودسازی خودکار IP به دلیل درخواست‌های مشکوک',
    result: 'موفق',
    timestamp: '1405/04/20 - ۱ روز پیش',
  },
  'aud-5008': {
    eventType: 'رویداد امنیتی',
    action: 'تلاش دسترسی غیرمجاز به API خارجی',
    result: 'ناموفق',
    timestamp: '1405/04/19 - ۲ روز پیش',
  },
};

export const auditActorMock: Record<string, AuditActor> = {
  'aud-5001': { name: 'ادمین ارشد', role: 'Super Admin', tenant: 'سطح پلتفرم' },
  'aud-5002': { name: 'ادمین امنیت', role: 'Security Admin', tenant: 'سطح پلتفرم' },
  'aud-5003': { name: 'ناشناس', role: 'نامشخص', tenant: 'نامشخص' },
  'aud-5004': { name: 'ادمین ارشد', role: 'Super Admin', tenant: 'سطح پلتفرم' },
  'aud-5005': { name: 'ادمین پشتیبانی', role: 'Support Admin', tenant: 'تننت البرز' },
  'aud-5006': { name: 'ادمین مالی', role: 'Finance Admin', tenant: 'تننت گیلان' },
  'aud-5007': { name: 'فایروال برنامه وب', role: 'سیستم خودکار', tenant: 'سطح پلتفرم' },
  'aud-5008': { name: 'ناشناس', role: 'نامشخص', tenant: 'نامشخص' },
};

export const auditTargetMock: Record<string, AuditTarget> = {
  'aud-5001': { targetType: 'پنل ادمین', targetId: 'panel-rep-01', targetName: 'پنل ادمین نمایندگی' },
  'aud-5002': { targetType: 'کاربر', targetId: 'usr-2210', targetName: 'کاربر: نمایندگی البرز' },
  'aud-5003': { targetType: 'پنل ادمین', targetId: 'panel-super-01', targetName: 'پنل ادمین ارشد' },
  'aud-5004': { targetType: 'پیکربندی کمیسیون', targetId: 'com-tier-2', targetName: 'ساختار کمیسیون سطح ۲' },
  'aud-5005': { targetType: 'مشتری', targetId: 'cus-1044', targetName: 'مشتری: cus-1044' },
  'aud-5006': { targetType: 'کیف پول تننت', targetId: 'wal-gilan-01', targetName: 'کیف پول تننت: تننت گیلان' },
  'aud-5007': { targetType: 'آدرس IP', targetId: '91.207.6.18', targetName: 'IP: 91.207.6.18' },
  'aud-5008': { targetType: 'ماژول امنیتی', targetId: 'sec-3005', targetName: 'ماژول امنیتی: دسترسی API خارجی' },
};

export const auditMetadataMock: Record<string, AuditMetadata> = {
  'aud-5001': {
    ip: '185.23.14.90',
    userAgent: 'Windows - Chrome 126',
    extra: ['روش احراز هویت: دو مرحله‌ای', 'موقعیت تقریبی: تهران، ایران'],
  },
  'aud-5002': {
    ip: '78.39.201.4',
    userAgent: 'macOS - Safari 17',
    extra: ['دلیل ثبت‌شده: ارتقای موقت برای پشتیبانی ویژه'],
  },
  'aud-5003': {
    ip: '46.19.201.55',
    userAgent: 'Linux - Firefox 128',
    extra: ['تعداد تلاش: ۳', 'قفل موقت حساب: فعال شد'],
  },
  'aud-5004': {
    ip: '185.23.14.90',
    userAgent: 'Windows - Chrome 126',
    extra: ['مقدار قبلی: ۸٪', 'مقدار جدید: ۱۰٪'],
  },
  'aud-5005': {
    ip: '95.81.44.19',
    userAgent: 'iOS - Vania App',
    extra: ['کد تیکت مرتبط: TCK-4471'],
  },
  'aud-5006': {
    ip: '31.7.68.201',
    userAgent: 'Windows - Edge 124',
    extra: ['مبلغ برداشت: ۲۴,۰۰۰,۰۰۰ تومان'],
  },
  'aud-5007': {
    ip: '91.207.6.18',
    userAgent: 'نامشخص - درخواست خودکار',
    extra: ['تعداد درخواست در دقیقه: ۴۲۰', 'قانون فایروال اجراشده: WAF-RULE-118'],
  },
  'aud-5008': {
    ip: '46.100.12.77',
    userAgent: 'Linux - curl/8.4',
    extra: ['کلید API استفاده‌شده: نامعتبر'],
  },
};

export const auditTimelineMock: Record<string, AuditTimelineEvent[]> = {
  'aud-5001': [
    {
      id: 'atl-1',
      type: 'action',
      title: 'ورود موفق',
      date: '۱۰ دقیقه پیش',
      description: 'ورود ادمین ارشد از دستگاه ویندوز با احراز هویت دو مرحله‌ای',
    },
  ],
  'aud-5002': [
    {
      id: 'atl-2',
      type: 'alert',
      title: 'تغییر سطح دسترسی',
      date: '۴۵ دقیقه پیش',
      description: 'ارتقای سطح دسترسی کاربر نمایندگی البرز نیازمند بازبینی است',
    },
  ],
  'aud-5003': [
    { id: 'atl-3', type: 'alert', title: 'تلاش ناموفق اول', date: '۱ ساعت پیش', description: 'رمز عبور نادرست' },
    { id: 'atl-4', type: 'alert', title: 'تلاش ناموفق دوم', date: '۱ ساعت پیش', description: 'رمز عبور نادرست' },
    {
      id: 'atl-5',
      type: 'system',
      title: 'قفل موقت حساب',
      date: '۱ ساعت پیش',
      description: 'حساب به دلیل تلاش‌های ناموفق پیاپی موقتاً قفل شد',
    },
  ],
  'aud-5004': [
    {
      id: 'atl-6',
      type: 'action',
      title: 'ویرایش پیکربندی کمیسیون',
      date: '۳ ساعت پیش',
      description: 'درصد کمیسیون سطح ۲ از ۸٪ به ۱۰٪ تغییر یافت',
    },
  ],
  'aud-5005': [
    {
      id: 'atl-7',
      type: 'action',
      title: 'حذف حساب مشتری',
      date: '۵ ساعت پیش',
      description: 'حساب مشتری cus-1044 بر اساس درخواست انصراف حذف شد',
    },
  ],
  'aud-5006': [
    {
      id: 'atl-8',
      type: 'action',
      title: 'برداشت از کیف پول',
      date: '۱ روز پیش',
      description: 'برداشت موجودی از کیف پول تننت گیلان توسط ادمین مالی',
    },
  ],
  'aud-5007': [
    {
      id: 'atl-9',
      type: 'system',
      title: 'شناسایی رفتار مشکوک',
      date: '۱ روز پیش',
      description: 'حجم بالای درخواست از یک آدرس IP شناسایی شد',
    },
    {
      id: 'atl-10',
      type: 'system',
      title: 'مسدودسازی خودکار',
      date: '۱ روز پیش',
      description: 'آدرس IP توسط فایروال برنامه وب مسدود شد',
    },
  ],
  'aud-5008': [
    {
      id: 'atl-11',
      type: 'alert',
      title: 'تلاش دسترسی غیرمجاز',
      date: '۲ روز پیش',
      description: 'تلاش برای استفاده از کلید API نامعتبر برای دسترسی خارجی',
    },
  ],
};
